import {
    AUTH_REGION,
    AUTH_USER_POOL_WEB_CLIENT_ID,
} from '../utils/config/configs';
import { Auth, Hub } from "aws-amplify";
import { jwtDecode } from 'jwt-decode';
import React, { createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import logger from "../server/server";
import { AwsConfigAuth } from '../utils/config/auth';
import AuthService from '../utils/config/AuthService';
import openNotificationWithIcon from "../utils/hooks/openNotificationWithIcon";

// Configuração do Amplify
Auth.configure(AwsConfigAuth);

// Criação do Contexto
const AuthContext = createContext(null);

const authService = new AuthService(
    AUTH_USER_POOL_WEB_CLIENT_ID,
    AUTH_REGION,
);

// Hook personalizado
export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuthContext deve ser usado dentro de um AuthProvider");
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [tenant, setTenant] = useState(null);
    const [userSignIn, setUserSignIn] = useState(null);
    const [isLogging, setIsLogging] = useState(false);
    const [isError, setIsError] = useState(false);
    const [token, setToken] = useState(null);

    const [zones, setZones] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [tags, setTags] = useState([]);

    useEffect(() => {
        // Aqui você pode definir a lógica para pegar o usuário se ele já estiver autenticado
        const loadUserFromLocalStorage = () => {
            const token = localStorage.getItem('unified_nova_accessToken');
            if (token) {
                // Recupera outros dados do localStorage
                setUser(localStorage.getItem('unified_nova_user'));
                setTenant(JSON.parse(localStorage.getItem('unified_nova_tenant'))?.[0]);
            }
        };
        loadUserFromLocalStorage();
    }, []);

    // Funções de login e logout
    const login = async () => {
        console.log("Starting SSO authentication...");
        try {
            console.log("Calling Auth.federatedSignIn...");

            await Auth.federatedSignIn({
                customProvider: "NOVA-SAML"
            });
        } catch (e) {
            setIsError(true);
            console.error("Error trying to authenticate via SSO SAML:", e);
        }
    };

    const logout = async () => {
        try {
            console.log("logout")
            await clearLocalStorage();
            setUser(null);
            await Auth.signOut();
            logger.info('User ' + user + ' logged out successfully! Tenant: ' + tenant);
        } catch (error) {
            console.error('Erro no logout', error);
            logger.error('User: ' + user + ' Tenant: ' + tenant + ', Error on log out');
        }
    };

    const checkToken = async () => {
        const token = localStorage.getItem('unified_nova_accessToken');
        if (!token) {
            console.warn("Token not found in localStorage");
            return false;
        }

        try {
            const decodedToken = jwtDecode(token); // Decodifica o token
            const actualDate = Math.floor(Date.now() / 1000);
            if (decodedToken.exp < actualDate) {
                const loginType = localStorage.getItem('unified_nova_loginType');
                if (loginType === 'AD') {// Se o login for via AD, não há refresh token, então faz logout
                    logout();
                } else {// Se o login for via Cognito, tenta renovar o token atraves do refresh token
                    try {
                        console.log("Token expired, refreshing...");
                        const refreshToken = localStorage.getItem('unified_nova_refreshToken');
                        const newTokens = await authService.renewToken(refreshToken); // Renova o token
                        localStorage.setItem('unified_nova_accessToken', newTokens.AccessToken);
                        localStorage.setItem('unified_nova_refreshToken', newTokens.RefreshToken);
                        return true;
                    } catch (error) {
                        logout();
                        console.error("Error refreshing token:", error);
                        return false;
                    }
                }
            }
        } catch (error) {
            // Tratamento do erro de refresh token expirado ou inválido
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                if (errorData.error === "invalid_grant") {
                    // Refresh token expirado ou inválido
                    logout();
                }
            }
        }
        return true; // O token ainda é válido
    };

    const clearLocalStorage = () => {
        return new Promise((resolve, reject) => {
            try {
                localStorage.clear();
                resolve();
            } catch (error) {
                reject(error);
            }
        });
    };

    const value = {
        user,
        setUser,
        zones,
        setZones,
        permissions,
        tags,
        setTags,
        tenant,
        setTenant,
        login,
        logout,
        isLogging,
        isError,
        userSignIn,
        checkToken,//Validate token
        token,
        setToken
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};